import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { HuePicker, SketchPicker } from "react-color";

import {
    MainPageWithButtons,
    ContentSpace,
    FileUploadComponent,
    RemoveAction,
} from "../_components";
import { CustomizationStore } from "../_stores/CustomizationStore/CustomizationStore";
import { LocationCustomization } from "../_models/LocationCustomization";
import { Selectors } from "../_reducers/app.reducer";

export function CustomizationModule() {

    //LIBRARIES
    const { t } = useTranslation();
    const dispatch = useDispatch();

    //GLOBAL STATE
    let locationCustomization = LocationCustomization();
    locationCustomization = useSelector(Selectors.getLocationCustomization);
    const isUploading = useSelector(Selectors.getCustomizationIsUploading);

    //LOCAL STATE
    const [accentColor, setAccentColor] = useState(
        (locationCustomization && locationCustomization.accentColor)
        || "#000000"
    );

    const [headerColor, setHeaderColor] = useState(
        (locationCustomization && locationCustomization.headerColor)
        || "#000000"
    );

    //ACTION HANDLERS
    function handleUploadLogo(logo) {
        dispatch(CustomizationStore.actions.uploadLogo(logo));
    }

    function handleUploadBackground(logo) {
        dispatch(CustomizationStore.actions.uploadBackground(logo));
    }

    function handleRemoveLogo() {
        dispatch(CustomizationStore.actions.removeLogo());
    }

    function handleRemoveBackground() {
        dispatch(CustomizationStore.actions.removeBackground());
    }

    function handleChangeAccentColor(hexColor) {
        dispatch(CustomizationStore.actions.update(hexColor));
    }
    function handleChangeHeaderColor(hexColor) {
        dispatch(CustomizationStore.actions.update(undefined, hexColor));
    }

    function handleResetAccentColor() {
        setAccentColor("#000000");
        dispatch(CustomizationStore.actions.update(""));
    }
    function handleResetHeaderColor() {
        setHeaderColor("#000000");
        dispatch(CustomizationStore.actions.update(undefined, ""));
    }

    //VIEW
    return (
        <ContentSpace
            mainContent={
                <MainPageWithButtons
                    pageTitle={t("mod_customization_title")}
                    content={content()}
                />
            }
        />
    );

    function content() {
        return (
            <div>
                <div className="mt-1"/>
                {managedByPMSCustomizationMessage()}
                {headerColorSelectorSection()}
                <div className="mt-3"/>
                {accentColorSelectorSection()}
                <div className="mt-3"/>
                
                {imageUploadSection(
                    t("mod_customization_location_logo"),
                    locationCustomization && locationCustomization.logo,
                    handleUploadLogo,
                    handleRemoveLogo,
                    isUploading,
                )}

                <div className="mt-3"/>

                {imageUploadSection(
                    t("mod_customization_login_background"),
                    locationCustomization && locationCustomization.loginBackground,
                    handleUploadBackground,
                    handleRemoveBackground,
                    isUploading,
                )}
            </div>
        );
    }

    function managedByPMSCustomizationMessage() {
        if (!locationCustomization?.managedByPMSCustomization) {
            return null;
        }

        return (
            <div className="alert alert-info mb-3" role="alert">
                {t("mod_customization_managed_by_pms_customization")}
            </div>
        );
    }

    function headerColorSelectorSection() {
        return (
            <div>
                {titleFn(
                    t("mod_customization_header_color"),
                    !!(locationCustomization && locationCustomization.headerColor),
                    handleResetHeaderColor,
                )}
                <div className="d-flex justify-content-center">
                    <SketchPicker
                        color={headerColor}
                        onChangeComplete={color => handleChangeHeaderColor(color.hex)}
                        onChange={color => setHeaderColor(color.hex)}
                        presetColors={[]}
                        disableAlpha
                    />
                </div>
            </div>
        );
    }

    function accentColorSelectorSection() {
        return (
            <div>
                {titleFn(
                    t("mod_customization_accent_color"),
                    !!(locationCustomization && locationCustomization.accentColor),
                    handleResetAccentColor,
                )}
                <div className="mt-1"/>
                <div className="d-flex justify-content-center my-2 cp">
                    <SketchPicker
                        color={accentColor}
                        onChangeComplete={color => handleChangeAccentColor(color.hex)}
                        onChange={color => setAccentColor(color.hex)}
                        presetColors={[]}
                        disableAlpha
                    />
                </div>
                <div className="d-flex c-h-3rem py-2">
                    {colorSwatch("c-bg-primary")}
                    {colorSwatch("c-bg-primary-light-1")}
                    {colorSwatch("c-bg-primary-light-2")}
                </div>
            </div>
        );

        function colorSwatch(backgroundCssClass) {
            return (
                <div className={`
                    col
                    border
                    rounded
                    mx-2
                    ${backgroundCssClass}
                `}/>
            );
        }
    }

    function imageUploadSection(title, img, handleUpload, handleRemove, isUploading) {
        return (
            <div>
                {titleFn(title)}
                <div className="mt-1"/>
                <FileUploadComponent
                    onUpload={handleUpload}
                    isUploading={isUploading}
                    fileName={img ? t("global_image_uploaded") : null}
                    onRemove={handleRemove}
                    preview={img &&
                        <div className="d-flex justify-content-center align-items-center">
                            <img
                                className="mw-100 c-mh-15rem rounded border bg-light p-2"
                                alt={title}
                                src={img}
                            />
                        </div>
                    }
                />
            </div>
        );
    }

    function titleFn(title, canRemove, handleRemove) {
        return (
            <div className="d-flex justify-content-between align-items-center">
                <div className="font-weight-bold text-uppercase">
                    {title}
                </div>
                <RemoveAction
                    label={t("global_remove")}
                    isVisible={!!canRemove}
                    onClick={handleRemove}
                />
            </div>
        );
    }
}